import { useState } from "react";
import { ShieldAlert, CheckCircle, CheckCheck, Download, Copy, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { refreshAuthToken } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { format, formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { SoundSelector } from "@/components/sound-selector";

interface SecurityNotification {
  id: number;
  userId: number;
  message: string;
  type: string;
  severity: string;
  isRead: boolean;
  readAt: string | null;
  metadata: Record<string, any>;
  createdAt: string;
  sourceIp: string | null;
  sourceUserAgent: string | null;
}

interface HtmlBackupFailure {
  projectId: string;
  error: string;
  errorCode?: string;
  durationMs?: number;
}

const formatDowntimeValue = (value: unknown, suffix = ''): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return `${value.toLocaleString()}${suffix}`;
};

const formatDowntimeDuration = (value: unknown): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  if (value < 1000) return `${Math.round(value)}ms`;
  const seconds = value / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
};

const formatDowntimeDate = (value: unknown): string => {
  if (!value) return 'Not recorded';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? String(value) : format(date, 'MMM d, yyyy HH:mm:ss');
};

/**
 * Build a copy-to-clipboard plain-text block for a security notification,
 * combining the human-readable message with any structured failure detail
 * carried in `metadata` (e.g. per-project HTML backup failures).
 */
export function buildCopyablePayload(n: SecurityNotification): string {
  // For backup_failure the spec calls for a canonical, pretty-printed JSON
  // payload that admins can paste straight into an issue tracker / chat /
  // log search. We emit exactly { message, metadata } — no human-readable
  // preamble — so the copy buffer is self-describing and machine-parsable.
  if (n.type === 'backup_failure') {
    return JSON.stringify(
      {
        message: n.message,
        metadata: n.metadata || {},
      },
      null,
      2,
    );
  }

  const lines: string[] = [];
  lines.push(`[${n.severity?.toUpperCase() || 'INFO'}] ${n.type}`);
  lines.push(new Date(n.createdAt).toISOString());
  lines.push('');
  lines.push(n.message);

  const md = n.metadata || {};
  if (n.type === 'process_downtime' && typeof md.copyText === 'string') {
    return md.copyText;
  }
  const htmlFailures: HtmlBackupFailure[] = Array.isArray(md.htmlFailures)
    ? md.htmlFailures
    : Array.isArray(md.backupRunStats?.htmlFailures)
      ? md.backupRunStats.htmlFailures
      : [];

  if (htmlFailures.length > 0) {
    lines.push('');
    lines.push(`Failed HTML backups (${htmlFailures.length}):`);
    for (const f of htmlFailures) {
      const code = f.errorCode ? ` [${f.errorCode}]` : '';
      const dur = typeof f.durationMs === 'number' ? ` (${f.durationMs}ms)` : '';
      lines.push(`  - ${f.projectId}${code}${dur}: ${f.error}`);
    }
  }

  if (md.dbBackupError) {
    lines.push('');
    lines.push(`Database backup error: ${md.dbBackupError}`);
  }

  if (n.sourceIp) {
    lines.push('');
    lines.push(`Source IP: ${n.sourceIp}`);
  }
  if (n.sourceUserAgent) {
    lines.push(`User-Agent: ${n.sourceUserAgent}`);
  }

  return lines.join('\n');
}

export function SecurityNotificationButton() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState<Record<number, boolean>>({});

  // Lightweight count query for badge (always enabled for instant updates)
  // Returns count AND highestSeverity for badge color logic
  const { data: countData } = useQuery<{ count: number; highestSeverity: string | null }>({
    queryKey: ['/api/security-notifications/count'],
    staleTime: 30000, // 30 seconds - stays fresh without excessive refetches
    refetchInterval: 60000, // Refetch every minute as fallback
  });

  const unreadCount = countData?.count || 0;
  const highestSeverity = countData?.highestSeverity;
  
  // Badge color: RED for critical/high severity, YELLOW for medium/low
  const isCriticalOrHigh = highestSeverity === 'critical' || highestSeverity === 'high';
  const badgeColor = isCriticalOrHigh ? 'bg-red-500' : 'bg-yellow-500';

  // Fetch full security notifications list (only when dropdown is open)
  const { data: securityNotifications = [], isLoading: isLoadingNotifications, refetch: refetchNotifications } = useQuery<SecurityNotification[]>({
    queryKey: ['/api/security-notifications', { unreadOnly: true }],
    enabled: isOpen, // Only fetch when dropdown is open
  });

  // Mark as read mutation
  const markReadMutation = useMutation({
    mutationFn: async (notificationId: number) => {
      const response = await apiRequest('PATCH', `/api/security-notifications/${notificationId}/read`, {});
      if (!response.ok) {
        throw new Error('Failed to mark notification as read');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/security-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/security-notifications/count'] });
      toast({
        title: "Success",
        description: "Security notification marked as read",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive",
      });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('PATCH', '/api/security-notifications/mark-all-read', {});
      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/security-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/security-notifications/count'] });
      toast({
        title: "Success",
        description: "All security notifications marked as read",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read",
        variant: "destructive",
      });
    },
  });

  // Notification action handler
  const handleMarkAsRead = (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    markReadMutation.mutate(notificationId);
  };

  const toggleDetails = (e: React.MouseEvent, notificationId: number) => {
    e.stopPropagation();
    setExpandedDetails(prev => ({ ...prev, [notificationId]: !prev[notificationId] }));
  };

  const handleCopyDetails = async (e: React.MouseEvent, notification: SecurityNotification) => {
    e.stopPropagation();
    const payload = buildCopyablePayload(notification);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        // Fallback for older browsers / insecure contexts
        const ta = document.createElement('textarea');
        ta.value = payload;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast({
        title: "Copied",
        description: "Notification details copied to clipboard",
      });
    } catch {
      toast({
        title: "Copy failed",
        description: "Could not copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const formatNotificationDate = (dateString: string): string => {
    const notificationDate = new Date(dateString);
    const now = new Date();
    const hoursDiff = (now.getTime() - notificationDate.getTime()) / (1000 * 60 * 60);
    
    // Show relative time for notifications within the last 24 hours
    if (hoursDiff < 24) {
      return formatDistanceToNow(notificationDate, { addSuffix: true });
    }
    
    // Show absolute date for older notifications
    return format(notificationDate, 'MMM d, yyyy HH:mm');
  };

  // Get severity badge color
  const getSeverityBadgeColor = (severity: string): string => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500';
      case 'high':
        return 'bg-orange-500';
      case 'medium':
        return 'bg-yellow-500';
      case 'low':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Accent (item background, hover, left border, and type badge) driven by the
  // notification's severity. Success notifications (e.g. backup_success) render
  // green so positive events are not shown with an alarming red treatment.
  const getNotificationAccent = (
    notification: SecurityNotification,
  ): { container: string; typeBadge: string } => {
    const isSuccess = /_success$/i.test(notification.type || '');
    if (isSuccess) {
      return {
        container: 'bg-green-500/10 hover:bg-green-500/20 border-l-2 border-green-500',
        typeBadge: 'bg-green-600',
      };
    }
    switch (notification.severity) {
      case 'critical':
        return {
          container: 'bg-red-500/10 hover:bg-red-500/20 border-l-2 border-red-500',
          typeBadge: 'bg-red-500',
        };
      case 'high':
        return {
          container: 'bg-orange-500/10 hover:bg-orange-500/20 border-l-2 border-orange-500',
          typeBadge: 'bg-orange-500',
        };
      case 'medium':
        return {
          container: 'bg-yellow-500/10 hover:bg-yellow-500/20 border-l-2 border-yellow-500',
          typeBadge: 'bg-yellow-500',
        };
      case 'low':
      default:
        return {
          container: 'bg-gray-500/10 hover:bg-gray-500/20 border-l-2 border-gray-500',
          typeBadge: 'bg-gray-500',
        };
    }
  };

  // Filter and sort security notifications
  const sortedNotifications = [...securityNotifications]
    .filter(notification => !notification.isRead)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Handle dropdown open/close with manual refetch as fallback
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    
    // When opening the dropdown, manually refetch to ensure fresh data
    if (newOpen) {
      refetchNotifications();
    }
  };

  // Download alert history as CSV
  const handleDownloadHistory = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      let response = await fetch('/api/security-notifications/export', {
        credentials: 'include',
      });
      
      // If 401, try to refresh token once
      if (response.status === 401) {
        const refreshed = await refreshAuthToken();
        if (refreshed) {
          response = await fetch('/api/security-notifications/export', {
            credentials: 'include',
          });
        }
      }
      
      if (!response.ok) {
        throw new Error('Failed to download alert history');
      }
      
      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-alerts-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: "Success",
        description: "Alert history downloaded successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download alert history",
        variant: "destructive",
      });
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="relative navy-card border-gray-600 text-gray-300 hover:bg-gray-700"
          data-testid="button-security-notification"
        >
          <ShieldAlert className="w-4 h-4" />
          {unreadCount > 0 && (
            <span 
              className={cn(
                "absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full",
                badgeColor
              )}
              data-testid="badge-security-count"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[450px] bg-gray-800 border-gray-600 max-h-[780px] overflow-y-auto"
      >
        <DropdownMenuLabel className="flex flex-col gap-2 text-gray-300">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" />
              Security Notifications ({sortedNotifications.length})
            </span>
            <SoundSelector soundType="soundSecurityNotifications" label="Sound" />
          </div>
          {sortedNotifications.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-gray-400 hover:text-gray-200 self-end"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
              data-testid="button-mark-all-security-read"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark All as Seen
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-gray-700" />
        
        {isLoadingNotifications ? (
          <div className="p-4 text-center text-gray-400">
            Loading security notifications...
          </div>
        ) : sortedNotifications.length === 0 ? (
          <div className="p-4 text-center text-gray-400">
            No unread security notifications
          </div>
        ) : (
          <div className="max-h-[680px] overflow-y-auto">
            {sortedNotifications.map((notification) => {
              const accent = getNotificationAccent(notification);
              return (
              <DropdownMenuItem
                key={notification.id}
                className={cn("flex flex-col gap-2 p-3 cursor-default", accent.container)}
                onSelect={(e) => e.preventDefault()}
                data-testid={`security-notification-${notification.id}`}
              >
                <div className="flex items-start justify-between gap-2 w-full">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge className={cn("text-xs text-white border-0", getSeverityBadgeColor(notification.severity))}>
                        {notification.severity}
                      </Badge>
                      <Badge className={cn("text-xs text-white border-0", accent.typeBadge)}>
                        {notification.type}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-200 whitespace-pre-wrap break-words">
                      {notification.message}
                    </p>
                    {notification.sourceIp && (
                      <p className="text-xs text-gray-400 mt-1">
                        Source IP: {notification.sourceIp}
                      </p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-gray-300 font-medium">
                        {formatNotificationDate(notification.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Structured failure details (expandable). Shown when the
                    notification metadata carries per-project HTML backup
                    failures, a DB backup error, or other debug context. */}
                {(() => {
                  const md = notification.metadata || {};
                  const htmlFailures: HtmlBackupFailure[] = Array.isArray(md.htmlFailures)
                    ? md.htmlFailures
                    : Array.isArray(md.backupRunStats?.htmlFailures)
                      ? md.backupRunStats.htmlFailures
                      : [];
                  const hasDbError = typeof md.dbBackupError === 'string' && md.dbBackupError.length > 0;
                  const isBackupFailure = notification.type === 'backup_failure';
                  const isProcessDowntime = notification.type === 'process_downtime';
                  const hasDetails = htmlFailures.length > 0 || hasDbError || isBackupFailure || isProcessDowntime;
                  if (!hasDetails) return null;
                  const isExpanded = !!expandedDetails[notification.id];
                  return (
                    <div className="w-full pt-2 border-t border-gray-700">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1 text-xs text-gray-300 hover:text-gray-100 hover:bg-gray-700/50 w-full justify-start"
                        onClick={(e) => toggleDetails(e, notification.id)}
                        data-testid={`button-toggle-details-${notification.id}`}
                      >
                        {isExpanded ? <ChevronDown className="w-3 h-3 mr-1" /> : <ChevronRight className="w-3 h-3 mr-1" />}
                        {isProcessDowntime
                          ? 'Process downtime report'
                          : isBackupFailure
                          ? 'Backup failure details'
                          : htmlFailures.length > 0
                            ? `Failed backups (${htmlFailures.length})`
                            : 'Error details'}
                      </Button>
                      {isExpanded && (
                        <div className="mt-1 max-h-64 overflow-y-auto rounded bg-gray-900/60 border border-gray-700 p-2 text-xs font-mono text-gray-200">
                           {isProcessDowntime && (
                             <div className="space-y-3" data-testid={`process-downtime-report-${notification.id}`}>
                               <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                                 <div>
                                   <div className="text-gray-500 uppercase tracking-wide text-[10px]">Detected</div>
                                   <div className="text-gray-100">{formatDowntimeDate(md.detectedAt)}</div>
                                 </div>
                                 <div>
                                   <div className="text-gray-500 uppercase tracking-wide text-[10px]">Recovered</div>
                                   <div className="text-gray-100">{formatDowntimeDate(md.recoveredAt)}</div>
                                 </div>
                                 <div>
                                   <div className="text-gray-500 uppercase tracking-wide text-[10px]">Silenced</div>
                                   <div className="text-gray-100">{formatDowntimeDuration(md.silenceMs)}</div>
                                 </div>
                                 <div>
                                   <div className="text-gray-500 uppercase tracking-wide text-[10px]">Stall timeout</div>
                                   <div className="text-gray-100">{formatDowntimeDuration(md.stallTimeoutMs)}</div>
                                 </div>
                                 <div>
                                   <div className="text-gray-500 uppercase tracking-wide text-[10px]">Force-kill delay</div>
                                   <div className="text-gray-100">{formatDowntimeDuration(md.forceKillDelayMs)}</div>
                                 </div>
                               </div>

                               <div className="border-t border-gray-700 pt-2">
                                 <div className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">Previous process</div>
                                 <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-300">
                                   <span><span className="text-gray-500">PID</span> {String(md.previousProcess?.pid ?? '—')}</span>
                                   <span><span className="text-gray-500">Node</span> {String(md.previousProcess?.nodeVersion ?? '—')}</span>
                                   <span className="col-span-2"><span className="text-gray-500">Started</span> {formatDowntimeDate(md.previousProcess?.startedAt)}</span>
                                 </div>
                               </div>

                               <div className="border-t border-gray-700 pt-2">
                                 <div className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">Last metrics</div>
                                 <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-gray-300">
                                   <span><span className="text-gray-500">RSS</span> {formatDowntimeValue(md.lastMetrics?.rssMb, ' MB')}</span>
                                   <span><span className="text-gray-500">Heap used</span> {formatDowntimeValue(md.lastMetrics?.heapUsedMb, ' MB')}</span>
                                   <span><span className="text-gray-500">Heap total</span> {formatDowntimeValue(md.lastMetrics?.heapTotalMb, ' MB')}</span>
                                   <span><span className="text-gray-500">External</span> {formatDowntimeValue(md.lastMetrics?.externalMb, ' MB')}</span>
                                   <span><span className="text-gray-500">Buffers</span> {formatDowntimeValue(md.lastMetrics?.arrayBuffersMb, ' MB')}</span>
                                   <span><span className="text-gray-500">Uptime</span> {formatDowntimeValue(md.lastMetrics?.uptimeSeconds, ' s')}</span>
                                 </div>
                               </div>

                               {Array.isArray(md.recentActivity) && md.recentActivity.length > 0 && (
                                 <div className="border-t border-gray-700 pt-2">
                                   <div className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">Recent activity</div>
                                   <div className="space-y-1.5">
                                     {md.recentActivity.map((activity: { at?: unknown; category?: unknown; summary?: unknown }, index: number) => (
                                       <div key={`${notification.id}-activity-${index}`} className="border-l-2 border-amber-400/60 pl-2">
                                         <div className="flex items-baseline justify-between gap-2">
                                           <span className="text-amber-200">{String(activity.category ?? 'Activity')}</span>
                                           <span className="text-[10px] text-gray-500">{formatDowntimeDate(activity.at)}</span>
                                         </div>
                                         <div className="text-gray-300 whitespace-pre-wrap break-words">{String(activity.summary ?? '—')}</div>
                                       </div>
                                     ))}
                                   </div>
                                 </div>
                               )}
                             </div>
                           )}
                          {hasDbError && (
                            <div className="mb-2">
                              <div className="text-amber-300 font-semibold">Database backup error</div>
                              <div className="whitespace-pre-wrap break-words text-gray-300">{String(md.dbBackupError)}</div>
                            </div>
                          )}
                          {isBackupFailure && (
                            <div className="mb-2">
                              <div className="text-amber-300 font-semibold mb-1">
                                Backup failed
                                {md.isRepeat ? ' (repeat within 24h — escalated to critical)' : ''}
                              </div>
                              <div className="text-gray-300 mb-1">
                                <span className="text-gray-400">Project:</span> {String(md.projectName || md.projectId || '—')}
                                {md.projectId && md.projectName ? ` (${String(md.projectId)})` : ''}
                              </div>
                              <div className="text-gray-300 mb-1">
                                <span className="text-gray-400">Type:</span> {String(md.backupType || '—')}
                                {' · '}
                                <span className="text-gray-400">By:</span> {String(md.triggeredBy || '—')}
                                {typeof md.durationMs === 'number' ? <> {' · '}<span className="text-gray-400">Duration:</span> {md.durationMs}ms</> : null}
                              </div>
                              {md.errorMessage && (
                                <div className="text-gray-300 mb-1 whitespace-pre-wrap break-words">
                                  <span className="text-gray-400">Error:</span> {String(md.errorMessage)}
                                  {md.errorCode ? <span className="text-gray-400"> [{String(md.errorCode)}]</span> : null}
                                </div>
                              )}
                              {md.progress && (
                                <div className="text-gray-300 mb-1">
                                  <span className="text-gray-400">Progress:</span>{' '}
                                  {Number(md.progress.cacheHits ?? 0)} cache hits, {Number(md.progress.freshlyCompiled ?? 0)} recompiled
                                  {' / '}{Number(md.progress.totalPulses ?? 0)} pulses
                                  {md.progress.lastPulseId != null ? ` · last pulseId=${md.progress.lastPulseId}` : ''}
                                </div>
                              )}
                              {md.stackTrace && (
                                <details className="mt-1">
                                  <summary className="cursor-pointer text-gray-400 hover:text-gray-200">Stack trace</summary>
                                  <pre className="mt-1 whitespace-pre-wrap break-words text-gray-300 text-[10px] leading-tight">{String(md.stackTrace)}</pre>
                                </details>
                              )}
                              <details className="mt-2">
                                <summary className="cursor-pointer text-gray-400 hover:text-gray-200">Raw JSON (copy-friendly)</summary>
                                <pre className="mt-1 whitespace-pre-wrap break-words text-gray-300 text-[10px] leading-tight select-all">{JSON.stringify(md, null, 2)}</pre>
                              </details>
                            </div>
                          )}
                          {htmlFailures.length > 0 && (
                            <div>
                              <div className="text-amber-300 font-semibold mb-1">Failed HTML backups</div>
                              <ul className="space-y-1">
                                {htmlFailures.map((f, i) => (
                                  <li key={`${notification.id}-fail-${i}`} className="border-l-2 border-red-500/50 pl-2">
                                    <div className="text-gray-100">
                                      <span className="text-red-300">{f.projectId}</span>
                                      {f.errorCode ? <span className="text-gray-400"> [{f.errorCode}]</span> : null}
                                      {typeof f.durationMs === 'number' ? <span className="text-gray-500"> ({f.durationMs}ms)</span> : null}
                                    </div>
                                    <div className="text-gray-300 whitespace-pre-wrap break-words">{f.error}</div>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Action buttons */}
                <div className="flex items-center gap-2 w-full pt-2 border-t border-gray-700">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-blue-300 hover:text-blue-200 hover:bg-blue-500/10"
                    onClick={(e) => handleCopyDetails(e, notification)}
                    data-testid={`button-copy-details-${notification.id}`}
                    title="Copy message and details to clipboard"
                  >
                    <Copy className="w-3 h-3 mr-1" />
                    Copy
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-green-400 hover:text-green-300 hover:bg-green-500/10 flex-1"
                    onClick={(e) => handleMarkAsRead(e, notification.id)}
                    disabled={markReadMutation.isPending}
                    data-testid={`button-mark-read-${notification.id}`}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Mark as Read
                  </Button>
                </div>
              </DropdownMenuItem>
              );
            })}
          </div>
        )}
        
        {/* Download Alert History Button */}
        <DropdownMenuSeparator className="bg-gray-700" />
        <DropdownMenuItem
          className="flex items-center gap-2 p-3 cursor-pointer text-gray-300 hover:bg-gray-700"
          onSelect={(e) => e.preventDefault()}
          onClick={handleDownloadHistory}
          data-testid="button-download-alert-history"
        >
          <Download className="w-4 h-4" />
          <span>Download alert history</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
